<template>
  <div class="subscription-details mb-xl md:mb-2xl">
    <div class="flex items-center justify-between flex-wrap gap-sm mb-xl md:mb-2xl">
      <h2 class="marketplace-heading m-0">
        {{ 'Subscription' | t }} #{{ subscription?.number }}
      </h2>
      <SloLink
        :to="$route.query.back || {name: 'marketplace-account-subscriptions'}"
      >
        <SloIcon name="ArrowLeft" class="mr-xs" />
        <span class="text-base fw-strong">{{ 'Back' | t }}</span>
      </SloLink>
    </div>
    <div class="mt-lg">
      <MarketplaceTable>
        <template v-slot:header>
          <MarketplaceTableHeader>
            <template v-slot:titles>
              <div class="flex items-start justify-between md:flex-col md:justify-start">
                <h4 class="text-base fw-strong">{{ 'Subscription Number' | t }}</h4>
                <span class="text-base c-gray-700">{{ subscription?.number }}</span>
              </div>
              <div class="flex items-start justify-between md:flex-col md:justify-start">
                <h4 class="text-base fw-strong">{{ 'Subscription Started' | t }}</h4>
                <span class="text-base c-gray-700">{{ subscription?.startDate }}</span>
              </div>
            </template>
            <template v-slot:buttons>
              <SubscriptionCancelButton
                v-if="!subscription?.isCancelled"
                :disabled="subscription?.isSubscriptionDisabled"
                :subscription-id="subscription?.number"
              />
            </template>
          </MarketplaceTableHeader>
        </template>
        <template v-slot:order-details-col-1>
          <div class="flex items-center justify-between flex-wrap gap-xs md:justify-start">
            <h4 class="m-0">
              {{ 'Shipping Address' | t }}
            </h4>
            <SubscriptionShippingAddressUpdateButton
              :disabled="subscription?.isSubscriptionDisabled"
              :subscription-id="subscription?.number"
              :current-shipping-address-id="subscription?.shippingAddress?.id"
              @subscriptionUpdated="$emit('subscriptionUpdated')"
            />
          </div>
          <p>{{ subscription?.shippingAddress?.firstName }}</p>
          <p v-if="subscription?.shippingAddress?.attention">
            ATTN: {{ subscription?.shippingAddress?.attention }}
          </p>
          <p v-if="subscription?.shippingAddress?.companyName">
            {{ subscription?.shippingAddress?.companyName }}
          </p>
          <p>{{ subscription?.shippingAddress?.line1 }}</p>
          <p>{{ subscription?.shippingAddress?.line2 }}</p>
          <p>{{ subscription?.shippingAddress?.town }} {{ subscription?.shippingAddress?.region?.isocode }}, {{ subscription?.shippingAddress?.postalCode }}</p>
          <p>{{ subscription?.shippingAddress?.country?.isocode }}</p>
        </template>
        <template v-slot:order-details-col-2>
          &nbsp;
        </template>
        <template v-slot:order-details-col-3>
          <div class="flex items-center justify-between flex-wrap gap-xs md:justify-start">
            <h4 class="m-0">
              {{ 'Payment Method' | t }}
            </h4>
            <SubscriptionPaymentUpdateButton
              :disabled="subscription?.isSubscriptionDisabled"
              :subscription-id="subscription?.number"
              :current-payment-detail-id="subscription?.paymentInfo?.id"
              @subscriptionUpdated="$emit('subscriptionUpdated')"
            />
          </div>
          <div :class="{'card-is-expired c-red-400': isCardExpired(subscription?.paymentInfo)}">
            {{ 'Expires' | t }}: {{ subscription?.paymentInfo?.expiryMonth }}/{{ subscription?.paymentInfo?.expiryYear }}
            <span v-if="isCardExpired(subscription?.paymentInfo)">{{ '(expired)' | t }}</span>
          </div>
          <p class="flex flex-row items-center gap-sm flex-wrap lg:flex-nowrap">
            <span class="c-gray-900">
              <SloIcon
                :name="subscription?.paymentInfo?.cardType?.name | getCardTypeIconName"
                size="lg"
              />
            </span>
            <span class="text-capitalize no-wrap">{{ subscription?.paymentInfo?.cardType?.code }}</span>
            <span class="lg:no-wrap">•••• {{ subscription?.paymentInfo?.cardNumber | getCardNumberLastFourDigits }}</span>
          </p>
        </template>
        <template v-slot:body>
          <MarketplaceRow
            :title="subscription?.product?.code"
            :description="subscription?.product?.title"
            :sku="subscription?.product?.code"
            :product-type="subscription?.product?.type"
            :price="subscription?.product?.subscriptionPrice?.formattedValue"
            :uom="subscription?.product?.uom"
            :quantity="subscription?.quantity"
            :total="subscription?.total?.formattedValue"
            :subscription-id="subscription?.number"
            :subscription-frequency="subscription?.product?.subscriptionFrequency"
            :subscription-percentage-discount="subscription?.product?.subscriptionPercentageDiscount"
          >
            <template v-slot:image>
              <SloProductImage :source="subscription?.product?.images | getProductThumbnail" />
            </template>
            <template v-slot:subscription-update>
              <SubscriptionUpdateButton
                :disabled="subscription?.isSubscriptionDisabled"
                :subscription-id="subscription?.number"
                :subscription-frequencies="subscription?.product?.subscriptionFrequencies"
                :current-quantity="subscription?.quantity"
                :current-subscription-frequency-name="subscription?.product?.subscriptionFrequency"
                @subscriptionUpdated="$emit('subscriptionUpdated')"
              />
            </template>
            <template v-slot:subscription-next-shipment>
              <div class="flex flex-col items-start gap-sm md:flex-row md:items-center">
                <div class="flex items-center gap-sm">
                  <SloIcon name="Timer" class="c-green-400" />
                  <span class="text-base fw-strong">
                    {{ 'Next Ship Date:' | t }} {{ subscription?.nextShipDate }}
                  </span>
                </div>
                <SubscriptionSkipNextShipmentButton
                  :disabled="subscription?.isSubscriptionDisabled"
                  :subscription-id="subscription?.number"
                  @subscriptionUpdated="$emit('subscriptionUpdated')"
                />
              </div>
            </template>
          </MarketplaceRow>
        </template>
        <template v-slot:footer>
        <MarketplaceTableFooter>
          <div class="marketplace-table--subscription-details-footer">
            <p class="m-0 text-sm fw-strong">
              {{ 'Purchased from Sloan Marketplace' | t }}
            </p>
            <div class="w-100">
              <div class="subscription-details--subtotal">
                <p>{{ 'Subtotal' | t }}</p>
                <p>{{ subscription?.subTotal?.formattedValue }}</p>
                <p>{{ 'Shipping' | t }}</p>
                <p>{{ subscription?.shippingCost?.formattedValue }}</p>
                <p>{{ 'Tax' | t }}</p>
                <p>{{ subscription?.tax?.formattedValue }}</p>
              </div>
              <hr>
              <div class="subscription-details--grand-total">
                <p class="fw-bold">{{ 'Total' | t }}</p>
                <p class="fw-bold">{{ subscription?.total?.formattedValue }}</p>
              </div>
            </div>
          </div>
        </MarketplaceTableFooter>
      </template>
      </MarketplaceTable>
    </div>
  </div>
</template>

<script>
import t from '../../main/i18n';
import {
  getProductThumbnail,
  getCardNumberLastFourDigits,
  getCardTypeIconName,
  formatSingleLineAddress,
  isCardExpired
} from '../../main/utils';
import SloProductImage from '../../../lib/components/SloProductImage.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloProductMeta from '../../../lib/components/SloProductMeta.vue';
import SloLink from '../../../lib/components/SloLink.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import MarketplaceTable from '../components/MarketplaceTable.vue';
import MarketplaceTableHeader from '../components/MarketplaceTableHeader.vue';
import MarketplaceRow from '../components/MarketplaceRow.vue';
import MarketplaceTableFooter from '../components/MarketplaceTableFooter.vue';
import SubscriptionUpdateButton from './SubscriptionUpdateButton.vue';
import SubscriptionSkipNextShipmentButton from './SubscriptionSkipNextShipmentButton.vue';
import SubscriptionShippingAddressUpdateButton from './SubscriptionShippingAddressUpdateButton.vue';
import SubscriptionPaymentUpdateButton from './SubscriptionPaymentUpdateButton.vue';
import SubscriptionCancelButton from './SubscriptionCancelButton.vue';

export default {
  components: {
    SubscriptionCancelButton,
    SloProductImage,
    SloButton,
    SloProductMeta,
    SloLink,
    SloIcon,
    MarketplaceTable,
    MarketplaceTableHeader,
    MarketplaceRow,
    MarketplaceTableFooter,
    SubscriptionUpdateButton,
    SubscriptionSkipNextShipmentButton,
    SubscriptionShippingAddressUpdateButton,
    SubscriptionPaymentUpdateButton
  },
  filters: {
    t,
    getProductThumbnail,
    getCardNumberLastFourDigits,
    getCardTypeIconName,
    formatSingleLineAddress
  },
  props: {
    subscription: {type: Object, required: true}
  },
  methods: {
    isCardExpired(paymentDetail) {
      return isCardExpired(paymentDetail);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace-table--subscription-details-footer {
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: space-between;
  gap: 1.6rem;
  width: 100%;

  @media (min-width: $screen-sm-min) {
    flex-direction: row;
  }

  > * {
    @media (min-width: $screen-sm-min) {
      flex: 1 1 50%;
    }
  }

  .subscription-details {
    &--subtotal, &--grand-total {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.6rem;

      p {
        margin: 0;

        &:nth-of-type(even) {
          place-self: end;
        }
      }
    }
  }
}

:deep(.marketplace-checkout--shipping-addresses) {
  margin-bottom: 0;
}
</style>
